import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Menu, Item, useContextMenu } from "react-contexify";
import "react-contexify/dist/ReactContexify.css";
import { useTranslate } from '@tolgee/react';

const CustomItem = ({ text, icon }) => {
  return (
    <CustomItemContainer>
      <p>{text}</p>
      {icon}
    </CustomItemContainer>
  );
};

const InputContextMenu = ({ inputRef, onCopy, onCut, onPaste }) => {
  const MENU_ID = "input-context-menu";
  const { show } = useContextMenu({ id: MENU_ID });
  const [hasSelection, setHasSelection] = useState(false);
  const isElectron = window.electron !== undefined;
  const { t } = useTranslate();
  const selectionRef = useRef({ start: 0, end: 0 });

  useEffect(() => {
    const handleUpdateSelection = (e) => {
      if (e.detail && e.detail.selection) {
        const { start, end } = e.detail.selection;
        selectionRef.current = { start, end };
        setHasSelection(start !== end);
      }
    };

    const textarea = inputRef.current;
    if (textarea) {
      textarea.addEventListener("update-selection", handleUpdateSelection);
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      selectionRef.current = { start, end };
      setHasSelection(start !== end);
      
      return () => {
        textarea.removeEventListener("update-selection", handleUpdateSelection);
      };
    }
  }, [inputRef.current]);

  const handleItemClick = ({ id }) => {
    switch (id) {
      case "copy":
        handleCopy();
        break;
      case "cut":
        handleCut();
        break;
      case "paste":
        handlePaste();
        break;
      default:
        break;
    }
  };

  const writeToClipboard = async (text) => {
    if (isElectron) {
      window.electron.clipboard.writeText(text);
    } else {
      await navigator.clipboard.writeText(text);
    }
  };

  const readFromClipboard = async () => {
    if (isElectron) {
      return window.electron.clipboard.readText();
    } else {
      return await navigator.clipboard.readText();
    }
  };

  const handleCopy = async () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const text = textarea.value.substring(
      selectionRef.current.start,
      selectionRef.current.end
    );
    if (!text) return;

    try {
      await writeToClipboard(text);
      onCopy && onCopy();
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleCut = async () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    textarea.focus();

    try {
      textarea.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
      
      const selection = textarea.value.substring(selectionRef.current.start, selectionRef.current.end);
      if (!selection) return;

      await writeToClipboard(selection);
      const success = document.execCommand('delete');
      if (!success) {
        textarea.setRangeText('', selectionRef.current.start, selectionRef.current.end, 'start');
        textarea.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'deleteContent' }));
      }

      onCut && onCut({ currentTarget: textarea, target: textarea });
    } catch (err) {
      console.error('Cut failed:', err);
    }
  };

  const handlePaste = async () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    textarea.focus();

    try {
      textarea.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
      const text = await readFromClipboard();
      if (!text) return;
      const success = document.execCommand('insertText', false, text);
      if (!success) {
        textarea.setRangeText(text, textarea.selectionStart, textarea.selectionEnd, 'end');
        textarea.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
      }

      onPaste && onPaste({ currentTarget: textarea, target: textarea });
    } catch (err) {
      console.error('Paste failed:', err);
    }
  };

  const menuItems = [
    { id: "cut", text: t('general.cut'), disabled: !hasSelection },
    { id: "copy", text: t('general.copy'), disabled: !hasSelection },
    { id: "paste", text: t('general.paste'), disabled: false },
  ];

  return (
    <StyledMenu id={MENU_ID} className="styledMenu">
      {menuItems.map((item, index) => (
        <Item
          onClick={handleItemClick}
          id={item.id}
          key={index}
          disabled={item.disabled}
        >
          <CustomItem text={item.text} icon={item.icon} />
        </Item>
      ))}
    </StyledMenu>
  );
};

export default InputContextMenu;

const CustomItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  padding: 6px 10px;
  svg {
    height: 20px;
    width: 20px;
  }
`;

const StyledMenu = styled(Menu)`
  overflow: hidden;
  color: white;
  padding: 0;
  border-radius: 12px;
  background: ${({ theme }) => theme.menu.background} !important;
  
  .contexify_item {
    border-bottom: 0.5px solid rgba(255, 255, 255, 0.1);

    &:last-child {
      border-bottom: none;
      border-radius: 0 0 12px 12px;
    }
    
    &:first-child {
      border-radius: 12px 12px 0 0;
    }
  }

  .contexify_item-disabled {
    background: ${({ theme }) => theme.menu.background} !important;
    cursor: not-allowed;
    opacity: 1 !important;
    
    p, svg {
      opacity: 0.4;
    }

    &:hover > .contexify_itemContent,
    &:focus > .contexify_itemContent {
      background: ${({ theme }) => theme.menu.background};
      cursor: not-allowed;
    }
  }
  
  .contexify_item:not(.contexify_item-disabled):hover > .contexify_itemContent,
  .contexify_item:not(.contexify_item-disabled):focus > .contexify_itemContent {
    color: inherit;
    background: ${({ theme }) => theme.chat.selectedBackground};
    cursor: pointer;
  }
`;